'use client';

import {
  BlocksRenderer,
  type BlocksContent,
} from '@strapi/blocks-react-renderer';
import { useMemo, type ComponentProps } from 'react';
import { useOptionalRenderContext } from './RenderContextProvider';
import { spliceBlockPlaceholders } from './spliceBlockPlaceholders';
import { substituteHref } from './substituteHref';

interface RichTextProps {
  nodes?: BlocksContent;
  /** Render every link with `target="_blank"` (e.g. privacy notices inside a form the visitor is still filling in). */
  linksInNewTab?: boolean;
}

type Blocks = NonNullable<ComponentProps<typeof BlocksRenderer>['blocks']>;

// Tailwind's preflight resets `a` to `color: inherit`, so without a class an
// editor's link is indistinguishable from the text around it (SH-461). This is
// the default only: a widget's own `[&_a]:…` utility is a descendant selector
// and outranks this single class, so widgets that restyle links keep doing so.
const LINK_CLASS = 'text-primary hover:underline';

// A link whose `url` came back empty from `substituteHref` — an unresolved
// placeholder — keeps its text and loses only the anchor.
const BLOCKS: Blocks = {
  link: ({ children, url }) =>
    url ? (
      <a href={url} className={LINK_CLASS}>
        {children}
      </a>
    ) : (
      <>{children}</>
    ),
};

const NEW_TAB_BLOCKS: Blocks = {
  link: ({ children, url }) =>
    url ? (
      <a
        href={url}
        className={LINK_CLASS}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ) : (
      <>{children}</>
    ),
};

// Rich-text fields walk the tree and substitute text nodes plus the `url` of
// a `link` node. Everything else — slugs and other markup attributes — is left
// alone by construction. We deep-clone the tree pre-render and pass each leaf
// `text` through `substitutePlaceholders`. Before that, block-level
// placeholders (a paragraph that is exactly one `{city.intro}`-style token
// backed by a rich-text entity field) are spliced in, so their own text
// nodes take the same substitution pass.
//
// Link URLs are in scope because a privacy notice has to reach the Standort's
// own Datenschutzerklärung, and `{location.privacyPolicyPath}` in the href is
// the only way an editor can express that: one slug-less Location Template
// Page backs every Standort, so a typed URL is wrong for either the GmbH or
// the franchise half (30.03.03 § Placeholders, 10.02.09 § AC 1). The same
// mechanism links a template to the URL's own sub-pages — e.g.
// `/nachhilfe/{location.slug}/jobs/` (SH-497).
export function RichText({ nodes, linksInNewTab }: RichTextProps) {
  const ctx = useOptionalRenderContext();
  const transformed = useMemo<BlocksContent | undefined>(() => {
    if (!nodes || nodes.length === 0) {
      return nodes;
    }
    // Without a render context nothing substitutes, but a link target can
    // still hold a token — walk anyway so its anchor drops (70.11 § Link
    // Targets) while the text stays literal.
    if (!ctx) {
      return dropEmptyHeadings(walk(nodes, identity) as BlocksContent);
    }
    const spliced = spliceBlockPlaceholders(nodes, ctx.entities);
    return dropEmptyHeadings(
      walk(spliced, ctx.substitutePlaceholders) as BlocksContent,
    );
  }, [nodes, ctx]);
  if (!transformed || transformed.length === 0) {
    return null;
  }
  return (
    <BlocksRenderer
      content={transformed}
      blocks={linksInNewTab ? NEW_TAB_BLOCKS : BLOCKS}
    />
  );
}

const identity = (s: string) => s;

/**
 * An editor's heading block with no visible text renders as an empty `<hN>`
 * that still counts in the page's outline (70.15-Heading-Structure § 6), e.g.
 * a second, empty `<h1>`. Checked after substitution, so a heading that is
 * only an unresolved-to-empty token goes too.
 */
function dropEmptyHeadings(nodes: BlocksContent): BlocksContent {
  return nodes.filter(
    (node) => node.type !== 'heading' || hasText(node.children),
  );
}

function hasText(node: unknown): boolean {
  if (Array.isArray(node)) {
    return node.some(hasText);
  }
  if (node == null || typeof node !== 'object') {
    return false;
  }
  const obj = node as Record<string, unknown>;
  if (typeof obj.text === 'string' && obj.text.trim() !== '') {
    return true;
  }
  return hasText(obj.children);
}

function walk(node: unknown, substitute: (s: string) => string): unknown {
  if (Array.isArray(node)) {
    return node.map((n) => walk(n, substitute));
  }
  if (node == null || typeof node !== 'object') {
    return node;
  }
  const obj = node as Record<string, unknown>;
  const out: Record<string, unknown> = { ...obj };
  if (typeof obj.text === 'string') {
    out.text = substitute(obj.text);
  }
  // Only a link's own href. Any other attribute that happens to be named
  // `url` (a media node's, say) keeps passing through untouched.
  if (obj.type === 'link' && typeof obj.url === 'string') {
    out.url = substituteHref(obj.url, substitute);
  }
  if (Array.isArray(obj.children)) {
    out.children = walk(obj.children, substitute);
  }
  return out;
}
